import { axisTicks } from "../ui/chartAxis.js";
import { formatInstant, instantTitle } from "../ui/instant.js";
import { hourAxis, offsetLabel } from "../ui/hourAxis.js";

/**
 * FeedDetail
 *
 * `recentChanges[].timestamp` arrives as an RFC 3339 UTC instant and is shown in
 * the reader's timezone — this list is read against the reader's own clock ("did
 * that change happen before or after my customer called?"), and the change
 * history is the one place where a wrong-by-two-hours answer matters. The raw
 * instant stays in the cell's `title`.
 *
 * **The chart is a dial, not a timeline.** It answers "why does this feed's
 * schedule look the way it does?" by putting the three things that explain a
 * cadence on one hour-of-day axis: the changed events the scheduler learns from,
 * the polls it plans to spend in response, and the polls that have actually
 * happened today. Absolute instants would answer a different question — the
 * project dashboard already charts the last 24 hours that way — and a trailing
 * window cannot show a *time-of-day* habit at all, because the same hour appears
 * once per day and the reader wants the 28 observations summed.
 *
 * **Why two axes.** A day of polls and a month of changed events differ by two
 * orders of magnitude; on one scale the green series would be flat against the
 * floor and the chart would answer "does this feed ever change?" with "looks
 * like nothing, ever". Two scales are the honest way to show both, and they come
 * with a duty the copy states rather than hides: on two axes the *slopes* are
 * not comparable, so the legend names the axis of each series and the series are
 * drawn in different forms — a bar for polls, a thin bar for changed events —
 * rather than two similar shapes that invite a comparison that would be
 * meaningless.
 *
 * **Why the changed series is bars.** It is mostly zero with occasional single
 * changes, and a line through those points draws a rising or falling edge
 * between neighbouring hours, which is a claim the data does not make: one
 * hour's change says nothing about the next. A bar per hour states what happened
 * in that hour and nothing else.
 *
 * **Why "now" is a position and not an edge.** Two of the three series are
 * averages of the day across the whole statistics window, so there is no past or
 * future along this axis at all: the marker says which hour the reader is
 * looking up at the wall from, nothing more. The third series — today's polls —
 * genuinely only exists up to the hour in progress, and drawing the rest of the
 * day as zero would turn "not polled yet" into "nothing was polled". So those
 * hours are left blank and their tooltip says why.
 *
 * **Why the buckets are the engine's, not the reader's.** A feed is shared, so
 * the engine shapes one schedule for customers in different timezones and the
 * stored 24 buckets are in *its* wall clock. The chart rotates them onto the
 * reader's clock (see ui/hourAxis.js) and says which zone they came from — a
 * chart labelled "09:00" is otherwise read as the reader's own morning, which
 * for a Warsaw customer watching UTC buckets is two hours out. All three series
 * share that one zone, which is what lets them be rotated together.
 *
 * Props:
 *   feed — { projectName, externalId, internalId, active }
 *   percentile — number
 *   currentPollRate — string, e.g. "10/hr"
 *   baselineRate — string, e.g. "4/hr"
 *   diffQuality — 'Full' | 'Reduced (no UID)'
 *   plannedPollsByHour — number[24]; the scheduler's plan for the day in
 *              absolute polls per hour, indexed by hour in the bucket zone. The
 *              scheduler splits the feed's daily allowance in proportion to these
 *              weights, so their sum is the feed's whole day.
 *   observedPollsByHour — number[24]; the polls actually made in the current
 *              recording day, indexed the same way. The counters only run to the
 *              hour in progress, so the hours they have not reached are absent
 *              rather than zero.
 *   changedEventsByHour — number[24]; changed *events* (not change rows)
 *              detected in each hour of the day over the statistics window. It
 *              is the evidence the scheduler weights its plan by, so counting
 *              anything else would put the green bars and the blue on different
 *              quantities.
 *   histogramTimezone — IANA name of the zone those 24 buckets are recorded in
 *   histogramUtcOffsetMinutes — that zone's offset from UTC at request time
 *   recentChanges — [{ timestamp, description, status }]
 *   timeZone — IANA zone for the timestamps; defaults to the browser's. A prop
 *              only so tests can assert a conversion deterministically.
 *   viewerOffsetMinutes — the reader's own offset, for the same reason.
 *   now — Date; the instant the "now" marker is placed from. Injectable so a
 *              test can put the marker on a known hour.
 */

/**
 * The chart is drawn in a fixed virtual coordinate space and scaled to whatever
 * width it is given, so it fills its card instead of sitting in a corner of it.
 *
 * Both halves of that matter. The viewBox is *wide* (the panel's content column
 * is about 1080px), because a viewBox taller than the space it is scaled into
 * becomes the limiting dimension: with a 336×120 box and a fixed 120px height the
 * drawing could never grow past 336px however wide the card was. And the height
 * is derived from the viewBox rather than set, so the aspect ratio — not a pixel
 * height — decides how tall the chart ends up.
 *
 * The bottom margin is two rows because the axis carries two different things: a
 * tick every three hours, and the "now" label under its own dashed line. Stacking
 * them is what stops the two from colliding whenever the current hour happens to
 * be one of the labelled ones.
 */
const VIEW = { width: 1080, height: 196 };
const PADDING = { top: 16, right: 52, bottom: 34, left: 52 };

/**
 * Every third hour is labelled. One label per hour is unreadable at a half-width
 * card and useless at a full-width one; 0/6/12/18 alone leaves the reader
 * counting bands to find an afternoon hour. Eight labels is the most that stays
 * legible across the whole range this panel is used at.
 */
const LABEL_EVERY = 3;

export default function FeedDetail({
  feed,
  percentile,
  currentPollRate,
  baselineRate,
  diffQuality = "Full",
  plannedPollsByHour,
  observedPollsByHour,
  changedEventsByHour,
  histogramTimezone = "UTC",
  histogramUtcOffsetMinutes = 0,
  recentChanges = [],
  timeZone,
  viewerOffsetMinutes,
  now = new Date(),
}) {
  const planned = hourSeries(plannedPollsByHour);
  const observed = hourSeries(observedPollsByHour);
  const changed = hourSeries(changedEventsByHour);
  const observedReported = Array.isArray(observedPollsByHour);

  // The chart on the reader's clock: bars ordered by local time, labelled with
  // the local start of each bucket, and re-rooted so the dial begins at the
  // reader's own midnight rather than at whatever local hour the bucket zone's
  // midnight happens to be.
  const axis = hourAxis({
    weights: planned,
    bucketOffsetMinutes: histogramUtcOffsetMinutes,
    viewerOffsetMinutes,
    now,
  });
  const bars = dialOrder(axis.bars);

  // The hour the reader is in, on their own clock, and the last hour the
  // recording day has reached. The first places the marker; the second decides
  // which of today's bars exist at all.
  const localNowMinutes = mod(
    now.getUTCHours() * 60 + now.getUTCMinutes() + axis.viewerOffsetMinutes,
    24 * 60,
  );
  const bucketNowHour = Math.floor(
    mod(now.getUTCHours() * 60 + now.getUTCMinutes() + histogramUtcOffsetMinutes, 24 * 60) / 60,
  );
  const nowIndex = dialIndexAt(bars, localNowMinutes);

  const points = bars.map((bar, index) => {
    const measured = bar.bucket <= bucketNowHour;
    return {
      bar,
      index,
      planned: planned[bar.bucket] ?? 0,
      // A bar that has not happened yet is not a zero: it is absent, and the
      // tooltip says so rather than letting the floor read as "nothing polled".
      // A server that does not report the series at all gets no such claim —
      // there is nothing to say when the counters are missing entirely.
      observed: measured ? (observed[bar.bucket] ?? 0) : null,
      pending: observedReported && !measured,
      changed: changed[bar.bucket] ?? 0,
    };
  });

  const plannedTotal = points.reduce((sum, point) => sum + point.planned, 0);
  const observedTotal = points.reduce((sum, point) => sum + (point.observed ?? 0), 0);
  const changedTotal = points.reduce((sum, point) => sum + point.changed, 0);
  const empty = plannedTotal === 0 && observedTotal === 0 && changedTotal === 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <p style={{ margin: 0, fontWeight: 500, fontSize: "var(--text-md)" }}>
            {feed?.projectName} / {feed?.externalId}.ics
          </p>
          <p style={{ margin: 0, fontSize: "var(--text-xs)", color: "var(--text-secondary)", fontFamily: "var(--font-mono)" }}>
            ext_id: {feed?.externalId} · internal: {feed?.internalId}
          </p>
        </div>
        <span className={`ical-badge ${feed?.active ? "ical-badge-success" : "ical-badge-neutral"}`}>
          {feed?.active ? "Active" : "Paused"}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 20 }}>
        <MiniStat label="Change percentile" value={percentile != null ? `${percentile}th` : "—"} sub={percentile >= 90 ? "Top 10% most active" : undefined} />
        <MiniStat label="Current poll rate" value={currentPollRate} sub={`Baseline ${baselineRate}`} />
        <MiniStat label="Diff quality" value={diffQuality} sub={diffQuality === "Full" ? "UID present" : "Surrogate key in use"} />
      </div>

      <p style={{ fontWeight: 500, fontSize: "var(--text-base)", margin: "0 0 4px" }}>Polls and changes by hour of day</p>
      <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "0 0 8px" }}>
        Last 28 days. {histogramCaption(axis, histogramTimezone)}
      </p>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        {/*
          The legend names the axis of each series, because that is the whole
          caveat of a two-axis chart: the heights are not comparable with each
          other. The three shapes match the three drawn forms — a pale bar for
          the plan, a solid bar for what happened, a thin bar for changed events
          — so a reader can find a series in the picture from the swatch alone.
        */}
        <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 14, fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span
              aria-hidden="true"
              style={{ width: 10, height: 10, borderRadius: 2, background: "var(--activity-requests, #7fb3e8)", opacity: 0.35 }}
            />
            Planned polls (left axis): {plannedTotal.toLocaleString()}/day
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span aria-hidden="true" style={{ width: 6, height: 10, borderRadius: 1, background: "var(--activity-requests, #7fb3e8)" }} />
            Polls today so far (left axis): {observedTotal.toLocaleString()}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span aria-hidden="true" style={{ width: 3, height: 10, borderRadius: 1, background: "var(--activity-changes, #3f9c62)" }} />
            Changed events (right axis): {changedTotal.toLocaleString()}
          </span>
        </span>
        {/*
          The scale, now in the unit the data actually supports. The old chart
          could only say "share of the busiest hour" because the API sent nothing
          else; with the plan's absolute counts the axis is a count of polls, and
          this sentence is the label that makes the numbers on it readable.
        */}
        <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)" }}>
          Left ticks = polls in that hour; right ticks = changed events in that hour
        </span>
      </div>

      {empty ? (
        <div className="ical-card" style={{ marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: "var(--text-sm)", color: "var(--text-secondary)" }}>
            Nothing has been polled or changed for this feed yet. The chart fills in as the worker
            polls its calendar and the scheduler learns where the day's changes sit.
          </p>
        </div>
      ) : (
        <HourProfileChart points={points} nowIndex={nowIndex} nowLabel={minuteLabel(localNowMinutes)} />
      )}

      <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "6px 0 20px" }}>
        This is an average of the day, not a timeline: each bar is that hour added up over the last
        28 days, so there is no past or future along this axis — the dashed line only marks the hour
        you are in now. The series are drawn against two axes because the volumes differ by two
        orders of magnitude — a day of polls per feed against a handful of changed events a month —
        so their heights are not comparable with each other; the totals in the legend are. Pale bars
        are the scheduler's plan: it divides the feed's daily allowance in proportion to where the
        feed has historically changed, which is why a change at 21:00 shows up as more polls at
        21:00. Solid bars are what has actually been polled today (the current day in{" "}
        {histogramTimezone}), and they stop at the hour in progress — the hours the recording day has
        not reached are left blank rather than drawn as zero, because "not polled yet" and "nothing
        polled" are different facts.
        {changedTotal === 0
          ? " Nothing changed in this window: no green bar is drawn, which is a result and not a missing series."
          : ""}
        {plannedTotal === 0 && observedReported
          ? " This server did not report a plan, so only today's polls are drawn."
          : ""}
      </p>

      <p style={{ fontWeight: 500, fontSize: "var(--text-base)", margin: "0 0 8px" }}>Last 5 changes</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {recentChanges.map((c, i) => (
          <div key={i} className="ical-row" style={{ display: "grid", gridTemplateColumns: "210px 1fr 70px", gap: 12, fontSize: "var(--text-sm)" }}>
            <span
              style={{ color: "var(--text-secondary)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}
              title={instantTitle(c.timestamp, { timeZone })}
            >
              {formatInstant(c.timestamp, { timeZone })}
            </span>
            <span>{c.description}</span>
            <span style={{ textAlign: "right" }}>{c.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * HourProfileChart draws the 24 bands: the plan behind, today's polls inside it,
 * changed events to the right of both, and the "now" marker over the top.
 *
 * The three forms are deliberately not the same shape. The plan is the widest
 * bar because it is the envelope the day is spent inside; today's polls are a
 * narrower bar standing inside it, so "we did more than planned this hour" is
 * visible as a bar poking out rather than as a second overlapping series; and
 * changed events are a thin bar in the free space at the right of each band,
 * which keeps the two axes' marks from being read as one stack.
 */
function HourProfileChart({ points, nowIndex, nowLabel }) {
  const maxPolls = Math.max(...points.map((point) => Math.max(point.planned, point.observed ?? 0)), 0);
  const maxChanged = Math.max(...points.map((point) => point.changed), 0);
  const pollsTicks = axisTicks(maxPolls);
  const changedTicks = axisTicks(maxChanged);
  const pollsTop = pollsTicks[pollsTicks.length - 1] || 1;
  const changedTop = changedTicks[changedTicks.length - 1] || 1;

  const plotWidth = VIEW.width - PADDING.left - PADDING.right;
  const plotHeight = VIEW.height - PADDING.top - PADDING.bottom;
  const step = plotWidth / points.length;
  const floor = PADDING.top + plotHeight;

  const bandLeft = (index) => PADDING.left + step * index;
  const center = (index) => bandLeft(index) + step / 2;
  const heightOf = (value, top) => plotHeight * (value / top);

  const plannedWidth = step * 0.5;
  const observedWidth = step * 0.3;
  // Thin enough to read as "a bar per hour" rather than as a third column, and
  // bounded so a future narrower chart does not lose it.
  const changedWidth = Math.max(1.6, Math.min(7, step * 0.16));

  const marker = points[nowIndex];

  return (
    <svg
      viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
      role="img"
      aria-label="Planned polls, today's polls and changed events by hour of day, in your timezone"
      // No width/height attributes: the style decides, and `height: auto` is what
      // makes the viewBox's aspect ratio set the rendered height.
      style={{ display: "block", width: "100%", height: "auto", overflow: "visible" }}
    >
      {/* Grid at the polls axis' ticks, so a bar can be read off a line. Drawn
          first so the bars sit on top of it. */}
      {pollsTicks.map((tick) => {
        const y = floor - heightOf(tick, pollsTop);
        return (
          <g key={`grid-${tick}`}>
            <line
              x1={PADDING.left}
              x2={PADDING.left + plotWidth}
              y1={y}
              y2={y}
              stroke="var(--border)"
              strokeWidth="1"
              strokeDasharray={tick === 0 ? undefined : "2 3"}
            />
            <text x={PADDING.left - 8} y={y + 4} textAnchor="end" fontSize="12" fill="var(--text-muted)">
              {tick}
            </text>
          </g>
        );
      })}

      {/* The changed-events axis on the right, in the green the bars use. */}
      {changedTicks.map((tick) => {
        const y = floor - heightOf(tick, changedTop);
        return (
          <text key={`changed-${tick}`} x={PADDING.left + plotWidth + 8} y={y + 4} fontSize="12" fill="var(--activity-changes, #3f9c62)">
            {tick}
          </text>
        );
      })}

      {points.map((point) => {
        const x = center(point.index);
        const label = bandRange(point.bar);
        return (
          <g key={`band-${point.bar.bucket}`}>
            {/*
              Today's counters exist for the recording day's hours up to the one
              in progress. The rest of the dial is not "zero polls": an invisible
              target with an explanation is all it gets, so a reader who hovers
              the empty half learns why rather than concluding nothing was
              polled. `pointer-events: all` because a transparent fill would
              otherwise not be hit.
            */}
            {point.pending ? (
              <rect
                data-pending-hour={point.bar.bucket}
                x={bandLeft(point.index)}
                y={PADDING.top}
                width={step}
                height={plotHeight}
                fill="transparent"
                style={{ pointerEvents: "all" }}
              >
                <title>{`${label} · today's counters have not reached this hour yet, so it is blank rather than zero`}</title>
              </rect>
            ) : null}

            {point.planned > 0 ? (
              <rect
                data-planned={point.planned}
                data-bucket={point.bar.bucket}
                x={x - plannedWidth / 2}
                y={floor - heightOf(point.planned, pollsTop)}
                width={plannedWidth}
                height={heightOf(point.planned, pollsTop)}
                rx={Math.min(1.5, plannedWidth / 4)}
                fill="var(--activity-requests, #7fb3e8)"
                fillOpacity="0.35"
              >
                <title>{`${label} · plan: ${count(point.planned, "poll")} (left axis)`}</title>
              </rect>
            ) : null}

            {point.observed > 0 ? (
              <rect
                data-observed={point.observed}
                data-bucket={point.bar.bucket}
                x={x - observedWidth / 2}
                y={floor - heightOf(point.observed, pollsTop)}
                width={observedWidth}
                height={heightOf(point.observed, pollsTop)}
                rx={Math.min(1, observedWidth / 4)}
                fill="var(--activity-requests, #7fb3e8)"
              >
                <title>{`${label} · ${count(point.observed, "poll")} today (left axis)`}</title>
              </rect>
            ) : null}

            {/* Drawn last within the band, so a change is never hidden behind
                traffic — and in its own lane, so the two axes' heights are not
                read as one stack. */}
            {point.changed > 0 ? (
              <rect
                data-changed={point.changed}
                data-bucket={point.bar.bucket}
                x={x + step * 0.27}
                y={floor - heightOf(point.changed, changedTop)}
                width={changedWidth}
                height={heightOf(point.changed, changedTop)}
                rx={Math.min(1, changedWidth / 3)}
                fill="var(--activity-changes, #3f9c62)"
              >
                <title>{`${label} · ${count(point.changed, "changed event")} over the last 28 days (right axis)`}</title>
              </rect>
            ) : null}
          </g>
        );
      })}

      {/*
        The hour in progress, as a position on the dial rather than an edge: two
        of the three series are averages of the day, so there is no boundary here
        to mark. It is drawn at the centre of the hour's band to match the bars,
        and labelled on its own row so it cannot collide with the hour ticks.
      */}
      {marker ? (
        <g data-now-index={nowIndex}>
          <line
            x1={center(nowIndex)}
            x2={center(nowIndex)}
            y1={PADDING.top}
            y2={floor}
            stroke="var(--text-muted)"
            strokeWidth="1"
            strokeDasharray="3 3"
            opacity="0.85"
          >
            <title>{`The hour you are in now (${nowLabel}) — a position on the dial, not progress through it.`}</title>
          </line>
          <text x={center(nowIndex)} y={VIEW.height - 20} textAnchor="middle" fontSize="11" fill="var(--text-muted)">
            now
          </text>
        </g>
      ) : null}

      {/* Every third hour. The first is anchored from its left edge, because a
          centred label at the very start of the plot hangs half outside it. */}
      {points
        .filter((point) => point.index % LABEL_EVERY === 0)
        .map((point) => {
          const first = point.index === 0;
          const anchor = first ? "start" : "middle";
          return (
            <text
              key={`label-${point.bar.bucket}`}
              x={first ? bandLeft(point.index) : center(point.index)}
              y={VIEW.height - 6}
              textAnchor={anchor}
              fontSize="12"
              fill="var(--text-muted)"
            >
              {point.bar.label}
            </text>
          );
        })}
    </svg>
  );
}

/** hourSeries pads or trims an API series to exactly the 24 buckets the dial has. */
function hourSeries(values) {
  const series = Array(24).fill(0);
  if (!Array.isArray(values)) return series;
  for (let hour = 0; hour < series.length && hour < values.length; hour++) {
    const value = values[hour];
    series[hour] = typeof value === "number" && Number.isFinite(value) ? value : 0;
  }
  return series;
}

/**
 * dialOrder rotates the local bars so the dial starts at the reader's midnight.
 *
 * `hourAxis` sorts by local start time, which begins at whatever local hour the
 * bucket zone's midnight falls on — for a Warsaw reader on UTC buckets that is
 * 02:00, and a day axis that begins at 02:00 makes the reader do the rotation in
 * their head. The buckets are a cycle, so re-rooting loses nothing; for a zone
 * offset by a fraction of an hour no bar starts at midnight, and the band that
 * contains it is moved to the front and labelled with its true (half-past) start.
 */
function dialOrder(bars) {
  if (bars.length === 0) return bars;
  const midnight = bars.findIndex((bar) => bar.startMinutes === 0);
  const start = midnight >= 0 ? midnight : bars.length - 1;
  if (start <= 0) return bars;
  return [...bars.slice(start), ...bars.slice(0, start)];
}

/**
 * dialIndexAt finds the band a local time falls in, by counting whole hours from
 * the first band's start. It is that simple because the dial's bands tile the day
 * in order and each is exactly an hour wide.
 */
function dialIndexAt(bars, minutes) {
  if (bars.length === 0) return 0;
  const offset = mod(minutes - bars[0].startMinutes, 24 * 60);
  return Math.min(bars.length - 1, Math.floor(offset / 60));
}

/** bandRange names a band by its local hour, as "14:00–15:00". */
function bandRange(bar) {
  return `${bar.label}–${hourAxisLabel(bar.startMinutes + 60)}`;
}

/** count renders "4 polls" / "1 changed event", so a tooltip never reads "1 polls". */
function count(value, noun) {
  return `${value.toLocaleString()} ${noun}${value === 1 ? "" : "s"}`;
}

/** minuteLabel renders minutes-from-midnight as "HH:MM", for the now marker's title. */
function minuteLabel(minutes) {
  const total = mod(Math.round(minutes), 24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

/**
 * histogramCaption says which clock the bars are on, in as few words as it takes
 * to be true. When the deployment's buckets already match the reader's clock
 * there is nothing to explain; when they do not, the reader has to know both that
 * a shift happened and that a daylight-saving change inside the 28-day window
 * makes it approximate.
 */
function histogramCaption(axis, bucketTimezone) {
  if (axis.shiftMinutes === 0) {
    return `Recorded in ${bucketTimezone}, which is your clock too.`;
  }
  // "Recorded in UTC (UTC)" helps nobody: the offset is only worth printing when
  // it adds something the name does not already say.
  const source = bucketTimezone === "UTC" ? "UTC" : `${bucketTimezone} (${offsetLabel(axis.bucketOffsetMinutes)})`;
  return (
    `Recorded in ${source}, shown on your clock ` +
    `(${offsetLabel(axis.viewerOffsetMinutes)}, shifted by ${describeShift(axis.shiftMinutes)}). ` +
    "A daylight-saving change inside the window means part of it was at a different offset."
  );
}

const hourAxisLabel = (minutes) => {
  const total = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
};

/** describeShift renders the rotation as "2 h" / "5 h 30 min" / "−5 h". */
function describeShift(minutes) {
  const sign = minutes < 0 ? "−" : "+";
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const mins = absolute % 60;
  if (hours === 0) return `${sign}${mins} min`;
  return mins === 0 ? `${sign}${hours} h` : `${sign}${hours} h ${mins} min`;
}

function mod(value, modulus) {
  return ((value % modulus) + modulus) % modulus;
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="ical-card">
      <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", margin: "0 0 6px" }}>{label}</p>
      <p style={{ fontSize: "var(--text-2xl)", fontWeight: 500, margin: 0 }}>{value}</p>
      {sub && <p style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", margin: "4px 0 0" }}>{sub}</p>}
    </div>
  );
}
